import * as z from 'zod'

import { useRepoReplicache } from './use-repo-replicache'
import { useReplicacheQueryV2 } from './use-replicache-query'
import { invariant } from '@epic-web/invariant'
import { useQuery } from '@tanstack/react-query'
import { Commit, commitSchema } from '@basehub/data/replicache/index'
import { useRepoStore } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/repo-store'
import { repoSpaceMutators } from '@basehub/data/replicache/mutators/repo-space/mutators'

export const useCommits = (headCommitId: string | undefined) => {
  const { data, isLoading } = useRepoReplicache()
  const spaceId = useRepoStore((s) => s.spaceId)
  const { replicache } = data ?? {}
  const replicacheEnabled = Boolean(replicache) && !!headCommitId

  const replicacheQuery = useReplicacheQueryV2({
    key: ['commits-list', data?.repoId, headCommitId, 'replicache'],
    fetcher: async (tx) => {
      invariant(
        headCommitId,
        `headCommitId is undefined, this should not happen.`
      )
      const allCommits = await repoSpaceMutators.listCommits(tx)
      const commitsByIdMap = new Map(allCommits.map((c) => [c.id, c]))
      const headCommit = commitsByIdMap.get(headCommitId)

      if (!headCommit) {
        throw new Error('Head commit not found')
      }

      const lineage: Commit[] = []

      let currentCommit: Commit | null | undefined = headCommit
      while (currentCommit) {
        lineage.push(currentCommit)
        currentCommit = currentCommit.parentCommitId
          ? commitsByIdMap.get(currentCommit.parentCommitId)
          : null
      }

      return lineage
    },
    replicache,
    keepPreviousData: true,
    enabled: replicacheEnabled,
  })

  const fallbackEnabled =
    !!spaceId &&
    !isLoading &&
    !replicacheEnabled &&
    !replicacheQuery.data &&
    !!headCommitId

  const fallback = useQuery({
    queryKey: ['commits-list', data?.repoId, headCommitId, 'fallback'],
    queryFn: async () => {
      invariant(
        headCommitId,
        `headCommitId is undefined, this should not happen.`
      )
      invariant(spaceId, `spaceId is undefined, this should not happen.`)
      const commitsResponse = await fetch(
        `/api/ad-hoc/commits/?spaceId=${encodeURIComponent(
          spaceId
        )}&headCommitId=${encodeURIComponent(headCommitId)}`
      ).then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch commits')
        }

        return res.json()
      })

      return z.array(commitSchema).parse(commitsResponse)
    },
    staleTime: Infinity, // immutable
    enabled: fallbackEnabled,
  })

  if (fallbackEnabled) return fallback

  return replicacheQuery
}
